// Betonijerry acceptance copy. The DB legalDocuments rows
// BETONIJERRY_REQUEST_CONSENT / BETONIJERRY_OFFER_ACCEPTANCE (ownerAsiakasId=1349)
// are the source of truth, fetched at runtime by RequestForm / ValuDetail. The
// constants below render only when the fetch or parse fails, so the legal text
// always shows. parseStructuredDoc is pure (no Sentry side effect — callers
// detect a fallback via `result === fallback`).
// Spec: docs/superpowers/specs/2026-06-17-betonijerry-acceptance-copy-db-source-design.md

export const REQUEST_CONSENT_TYPE = "BETONIJERRY_REQUEST_CONSENT";
export const OFFER_ACCEPTANCE_TYPE = "BETONIJERRY_OFFER_ACCEPTANCE";

// Stamped on pumppuRequest.termsVersion when the DB fetch fails. Must equal the
// DB BETONIJERRY_REQUEST_CONSENT version whose content this fallback mirrors.
export const GATE1_FALLBACK_VERSION = "gate1-2026-07-18";

// Gate-1 content fallback (checkbox + Lue lisää). `version` is NOT part of the
// JSON block — it comes from the legalDocuments row version.
//
// i18n: a function of `t` (Task 18) — callers MUST memoize
// (useMemo(() => gate1ContentFallback(t), [t])) since useAcceptanceCopy's
// `fallback` param is a useEffect dependency compared by reference.
// @param {(key: string) => string} t
export function gate1ContentFallback(t) {
    return {
        checkboxLabel: t("legal.gate1Fallback.checkboxLabel"),
        details: [
            t("legal.gate1Fallback.details.marketplace"),
            t("legal.gate1Fallback.details.accuracy"),
            t("legal.gate1Fallback.details.leftoverAmount"),
            t("legal.gate1Fallback.details.noStructuralResponsibility"),
            t("legal.gate1Fallback.details.dataProcessing"),
            t("legal.gate1Fallback.details.contactVisibility"),
            t("legal.gate1Fallback.details.offerOrdering"),
        ],
    };
}

// Platform-standard cancellation terms — the SAME text on every offer, NOT a
// per-provider free-text field. The SOURCE OF TRUTH is the DB acceptance copy
// (legalDocuments BETONIJERRY_OFFER_ACCEPTANCE → `peruutusehdot`, versioned and
// editable via `ib legal` / the admin tool); this constant is only the offline
// FALLBACK, read by OfferCard / AcceptConfirmModal as `copy?.peruutusehdot ??
// PERUUTUSEHDOT_STANDARD`. Text verbatim from Kalle Urho Oy's current standard
// offer template ("…/TARJOUKSET/…TARJOUS POHJA.docx" → "Peruutus" section) — the
// committed concrete-pump supply runs on these terms, so the marketplace shows
// one consistent, real cancellation policy instead of an empty "Sovitaan
// tarkemmin". `summary` is the compact card line; `full` is the canonical text.
// Kept OUTSIDE GATE2_FALLBACK on purpose: it's read leniently (a `peruutusehdot`
// key in the DB doc is an "extra" key that passes shape validation), so adding
// it to the DB never falls the rest of the Gate-2 microcopy back.
// i18n: a function of `t` (Task 18) — same memoization requirement as
// gate1ContentFallback above.
// @param {(key: string) => string} t
export function peruutusehdotStandard(t) {
    return {
        summary: t("legal.peruutusehdotStandard.summary"),
        full: t("legal.peruutusehdotStandard.full"),
    };
}

// Gate-2 offer-acceptance microcopy fallback. ariaLabel stored explicitly
// (sentence-case) — NOT composed from the ALL-CAPS ctaLabel.
//
// i18n: a function of `t` (Task 18) — same memoization requirement as
// gate1ContentFallback above.
// @param {(key: string) => string} t
export function gate2Fallback(t) {
    return {
        ctaLabel: t("legal.gate2Fallback.ctaLabel"),
        ctaCaption: t("legal.gate2Fallback.ctaCaption"),
        ariaLabel: t("legal.gate2Fallback.ariaLabel"),
        confirmTitle: t("legal.gate2Fallback.confirmTitle"),
        responsibilityLabel: t("legal.gate2Fallback.responsibilityLabel"),
        responsibilityBody: t("legal.gate2Fallback.responsibilityBody"),
        fieldLabels: {
            provider: t("legal.gate2Fallback.fieldLabels.provider"),
            total: t("legal.gate2Fallback.fieldLabels.total"),
            cancellation: t("legal.gate2Fallback.fieldLabels.cancellation"),
            validUntil: t("legal.gate2Fallback.fieldLabels.validUntil"),
        },
    };
}

/**
 * Resolve the active cancellation terms from fetched acceptance copy, guarding
 * the sub-shape. Returns the DB-sourced `copy.peruutusehdot` ONLY when both
 * `summary` and `full` are non-empty strings; otherwise the bundled standard.
 *
 * This is the one seam `matchesShape` can't cover: `peruutusehdot` is an "extra"
 * key on the GATE2 doc, so a malformed operator publish (`{summary}` with no
 * `full`, `{}`, or a bare string) passes overall parse and is returned as live
 * `copy`. The field is legally load-bearing — KKV requires the cancellation
 * terms visible on the binding-accept surface — so a bad publish must degrade to
 * the standard text, never to an empty `<Typography>`. `copy?.x ?? FALLBACK`
 * alone only catches null/undefined; this catches the malformed-but-truthy case.
 * @param {object} copy
 * @param {(key: string) => string} t
 */
export function resolvePeruutusehdot(copy, t) {
    const p = copy?.peruutusehdot;
    if (p && typeof p.summary === "string" && p.summary && typeof p.full === "string" && p.full) {
        return p;
    }
    return peruutusehdotStandard(t);
}

// Recursively validate `value` against the shape of `template`: strings must be
// non-empty strings, arrays must be non-empty arrays of non-empty strings,
// objects must contain every template key (extra keys are ignored).
function matchesShape(value, template) {
    if (typeof template === "string") return typeof value === "string" && value.length > 0;
    if (Array.isArray(template)) {
        return Array.isArray(value) && value.length > 0 && value.every((v) => typeof v === "string" && v.length > 0);
    }
    if (template && typeof template === "object") {
        return !!value && typeof value === "object" && Object.keys(template).every((k) => matchesShape(value[k], template[k]));
    }
    return false;
}

/**
 * Parse a structured acceptance-copy doc (JSON in a fenced ```json block, or the
 * whole content as JSON). Returns the parsed object on success, or the SAME
 * `fallback` reference on any failure (empty/missing/malformed/shape-mismatch).
 * Callers detect a fallback via `result === fallback`. Pure — no side effects.
 */
export function parseStructuredDoc(markdownContent, fallback) {
    if (typeof markdownContent !== "string" || !markdownContent.trim()) return fallback;
    const fenced = markdownContent.match(/```json\s*([\s\S]*?)```/i);
    const raw = (fenced ? fenced[1] : markdownContent).trim();
    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return fallback;
    }
    if (!matchesShape(parsed, fallback)) return fallback;
    return parsed;
}
